import { Plus, RefreshCw, SearchX, Users } from "lucide-react";
import { useCallback, useMemo } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { AssignRolesDrawer } from "@/modules/dashboard/settings/components/access/AssignRolesDrawer";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { CreateUserDrawer } from "../components/CreateUserDrawer";
import { DeleteUserDialog } from "../components/DeleteUserDialog";
import { SuspendUserDialog } from "../components/SuspendUserDialog";
import { UserDetailDrawer } from "../components/UserDetailDrawer";
import { UsersCards } from "../components/UsersCards";
import { UsersNoRolesNote } from "../components/UsersNoRolesNote";
import { UsersPopulationStrip } from "../components/UsersPopulationStrip";
import { UsersTable } from "../components/UsersTable";
import { UsersToolbar } from "../components/UsersToolbar";
import { useUserDetail } from "../hooks/useUserDetail";
import { useUserMutations } from "../hooks/useUserMutations";
import { useUserRowViews } from "../hooks/useUserRowViews";
import { useUsersAccess } from "../hooks/useUsersAccess";
import { useUsersPageState } from "../hooks/useUsersPageState";
import { useUsersRoster } from "../hooks/useUsersRoster";
import { UserStatus } from "../types/create-user.types";
import type { CreateUserPayload, UpdateUserPayload } from "../types/create-user.types";
import type { UserListItem } from "../types/users-api.types";
import { resolveOrganizationCell } from "../utils/organizationCell";

/**
 * `/app/users` — the Coloca-staff console for every person with access to the platform.
 *
 * Access is decided by the backend: `users:read` on the listing, and a denial latches for the session into
 * `NoAccessView`. Note the guard throws `UnauthorizedException` for an insufficient permission, so it arrives
 * as **401, not 403** — both are latched, and neither is read as a dead session.
 *
 * The route is reachable by an organization member holding `users:read` (`internalOnlyMenu` hides the menu
 * item, not the route). For them the listing is pinned server-side to their own organization, so the
 * Organización column is dropped entirely: it would be a constant, and `GET /internal/organizations` is
 * `userTypes: [INTERNAL]` and would 403 anyway. One guaranteed failed request not sent.
 */
const UsersPage = () => {
  const { t } = useTranslations("users");
  const { t: tGlobal } = useTranslations("global");
  const { user, isInternalUser, can } = useAuth();

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_users"), to: "/app/users" },
      ],
      [tGlobal],
    ),
  );

  const roster = useUsersRoster();
  const access = useUsersAccess(roster.rows, can("roles:read"));
  const mutations = useUserMutations();
  const ui = useUsersPageState();

  const canWrite = can("users:write");
  const canDelete = can("users:delete");
  const canManageRoles = can("users:write") && can("roles:write");

  const selectedUser = ui.overlay.kind === "detail" ? ui.overlay.user : null;
  const detail = useUserDetail(selectedUser?.id ?? null);

  /**
   * The two handlers below feed `useUserRowViews`' dependency array, so they must not change identity on a
   * keystroke — and depending on the whole `roster` / `ui` / `mutations` objects made them do exactly that:
   * `roster` is a `useMemo` keyed on `query`, so typing one character rebuilt `handleVerifyEmail` and
   * `applyStatus`, then `handleSetStatus`, then all 25 row views and all 25 `actions` objects. These members
   * are individually stable (`useCallback` with empty or stable deps), which is what the memo needs.
   */
  const { refresh: refreshRoster } = roster;
  const { ping, openSuspend } = ui;
  const { verifyEmail, setStatus: setUserStatus } = mutations;

  const handleVerifyEmail = useCallback(
    async (target: UserListItem) => {
      const result = await verifyEmail(target.id);
      if (!result.ok) {
        colocaToast.error(result.error.message);
        return;
      }
      colocaToast.success(t("verify_email_success"));
      ping(target.id);
      refreshRoster();
    },
    [ping, refreshRoster, t, verifyEmail],
  );

  /** @returns Whether the change was applied — the suspend dialog stays open on a failure. */
  const applyStatus = useCallback(
    async (target: UserListItem, status: UserStatus) => {
      const result = await setUserStatus(target.id, status);
      if (!result.ok) {
        colocaToast.error(result.error.message);
        return false;
      }
      colocaToast.success(t("status_change_success"));
      ping(target.id);
      refreshRoster();
      return true;
    },
    [ping, refreshRoster, setUserStatus, t],
  );

  // Suspension is the one status change that gets a confirmation: it denies access outright, and the menu
  // item sits next to Delete. Activate and Deactivate are reversible in one click and stay direct.
  const handleSetStatus = useCallback(
    (target: UserListItem, status: UserStatus) => {
      if (status === UserStatus.SUSPENDED) {
        openSuspend(target);
        return;
      }
      void applyStatus(target, status);
    },
    [applyStatus, openSuspend],
  );

  // Closes AFTER the await, like Delete — `ColocaAlertDialog`'s contract is that confirming does not dismiss.
  // Dismissing first made `confirmDisabled={isBusy}` unreachable and left a failed suspension explained only
  // by a toast, with the dialog already gone.
  const handleConfirmSuspend = useCallback(async () => {
    if (ui.overlay.kind !== "suspend") return;
    if (await applyStatus(ui.overlay.user, UserStatus.SUSPENDED)) ui.closeOverlay();
  }, [applyStatus, ui]);

  const handleCreate = useCallback(
    async (payload: CreateUserPayload) => {
      const result = await mutations.createUser(payload);
      if (!result.ok) {
        // Bound to the drawer's inputs — a duplicate email belongs on the email field, not in a toast the
        // operator has to translate back into an action.
        ui.setMutationError(result.error);
        return;
      }
      colocaToast.success(t("create_success"));
      ui.closeOverlay();
      ui.ping(result.data.id);
      roster.refresh();
    },
    [mutations, roster, t, ui],
  );

  const handleSave = useCallback(
    async (payload: UpdateUserPayload) => {
      if (!selectedUser) return;
      // Nothing changed — do not fire a PATCH whose only outcome is proving the diff was empty.
      if (Object.keys(payload).length === 0) {
        ui.closeOverlay();
        return;
      }
      const result = await mutations.updateUser(selectedUser.id, payload);
      if (!result.ok) {
        ui.setMutationError(result.error);
        return;
      }
      colocaToast.success(t("detail_saved"));
      // Deliberately NOT trusting the echo: on an email change the service clears `email_verified_at` in
      // memory only, so the body omits a field the database still holds. Closing the drawer unmounts the
      // detail read entirely, and the roster refetch below is what brings the row back correct — calling
      // `detail.reload()` here would be dead code, since the id is null by the time its effect runs.
      ui.closeOverlay();
      ui.ping(selectedUser.id);
      roster.refresh();
    },
    [mutations, roster, selectedUser, t, ui],
  );

  const handleSetPassword = useCallback(
    async (password: string) => {
      if (!selectedUser) return;
      const result = await mutations.setPassword(selectedUser.id, password);
      if (!result.ok) {
        colocaToast.error(result.error.message);
        return;
      }
      colocaToast.success(t("password_success"));
      ui.closeOverlay();
      ui.ping(selectedUser.id);
    },
    [mutations, selectedUser, t, ui],
  );

  const handleDelete = useCallback(async () => {
    if (ui.overlay.kind !== "delete") return;
    const result = await mutations.deleteUser(ui.overlay.user.id);
    if (!result.ok) {
      colocaToast.error(result.error.message);
      return;
    }
    colocaToast.success(t("delete_success"));
    ui.closeOverlay();
    // Deleting the last row of a page beyond the first would otherwise refetch an offset past the end: zero
    // rows under a `total` still larger than one page, with no active filters, i.e. the "Todavía no hay
    // usuarios" empty state painted over a populated roster. Stepping back re-issues the listing on its own,
    // so only the counts need asking for.
    if (roster.rows.length === 1 && roster.page > 1) {
      roster.setPage(roster.page - 1);
      roster.refreshCounts();
    } else {
      roster.refresh();
    }
  }, [mutations, roster, t, ui]);

  // Referentially stable while the drawer is open — it re-seeds from this on every open.
  const accessPrincipal = ui.overlay.kind === "access" ? ui.overlay.user : null;

  /**
   * Role changes refresh the COUNTS but not the listing.
   *
   * `roster.refresh()` would re-issue `GET /internal/users` as well, and the new `rows` array invalidates
   * `rolesById`, which re-runs the 84-cell deny-first resolution for all 25 principals — while the drawer is
   * open and the table visibly dims behind it. Toggling five roles would cost ten requests and five full
   * recomputes. `no_roles` is the only figure a role change moves, so only that is refetched.
   *
   * The rejection is deliberately NOT handled here, and `AssignRolesDrawer.toggle` is the reason. It is the
   * only caller, it awaits inside its own try/catch, and that catch is what reverts the optimistic tick,
   * marks the row as failed AND toasts — so a local `catch` that toasted before re-throwing showed the same
   * 400 `role_not_visible` twice, stacked. Nothing else was in that catch: skipping `refreshCounts()` on a
   * failure needs no code, since it sits after the `await` and is unreachable once it rejects.
   *
   * Same shape as the other consumer of this drawer (`UsuariosSection`), which passes the mutations straight
   * through. Whoever owns the drawer owns the toast.
   */
  const handleAssignRoles = useCallback(
    async (roleIds: string[]) => {
      if (!accessPrincipal) return;
      await access.assignRoles(accessPrincipal.id, roleIds);
      roster.refreshCounts();
    },
    [access, accessPrincipal, roster],
  );

  const handleRemoveRole = useCallback(
    async (roleId: string) => {
      if (!accessPrincipal) return;
      await access.removeRole(accessPrincipal.id, roleId);
      roster.refreshCounts();
    },
    [access, accessPrincipal, roster],
  );

  // Hoisted out of the JSX: inline arrows are a new identity every render, which defeated the 25-row
  // view memo entirely and rebuilt every row on every keystroke.
  const handleOpenDetail = useCallback((target: UserListItem) => ui.openDetail(target), [ui]);
  const handleOpenAccess = useCallback((target: UserListItem) => ui.openAccess(target), [ui]);
  const handleOpenPassword = useCallback((target: UserListItem) => ui.openDetail(target, "password"), [ui]);
  const handleOpenDelete = useCallback((target: UserListItem) => ui.openDelete(target), [ui]);

  const { views, isDirectoryPending, directoryFailed, directoryTruncated, lookupOrganization } =
    useUserRowViews({
      rows: roster.rows,
      access,
      currentUserId: user?.id ?? null,
      showOrganization: isInternalUser,
      canWrite,
      canDelete,
      canManageRoles,
      onOpenDetail: handleOpenDetail,
      onManageAccess: handleOpenAccess,
      onVerifyEmail: handleVerifyEmail,
      onSetStatus: handleSetStatus,
      onSetPassword: handleOpenPassword,
      onDelete: handleOpenDelete,
    });

  const assignedRoleIds = useMemo(
    () => (accessPrincipal ? (access.rolesById[accessPrincipal.id] ?? []).map((role) => role.id) : []),
    [accessPrincipal, access.rolesById],
  );

  const organizationLabel = useMemo(() => {
    if (!selectedUser) return "—";
    const view = resolveOrganizationCell(
      selectedUser.organization_id,
      lookupOrganization(selectedUser.organization_id),
    );
    if (view.kind === "resolved") return view.name;
    if (view.kind === "root") return `${view.name} (${t("org_internal")})`;
    if (view.kind === "none") return t("org_none");
    return t("org_unresolved");
  }, [selectedUser, lookupOrganization, t]);

  if (roster.noAccess) return <NoAccessView />;

  const empty = roster.hasActiveFilters ? (
    <ColocaEmpty
      icon={SearchX}
      title={t("empty_filtered_title")}
      description={t("empty_filtered_body")}
      surface="card"
    >
      <ColocaButton variant="secondary" size="sm" onClick={roster.clearFilters}>
        {t("empty_clear_filters")}
      </ColocaButton>
    </ColocaEmpty>
  ) : (
    <ColocaEmpty icon={Users} title={t("empty_title")} description={t("empty_body")} surface="card">
      {canWrite && (
        <ColocaButton variant="primary" size="sm" icon={Plus} onClick={ui.openCreate}>
          {t("action_create")}
        </ColocaButton>
      )}
    </ColocaEmpty>
  );

  const footer =
    roster.total > roster.pageSize ? (
      <ColocaPagination
        page={roster.page}
        perPage={roster.pageSize}
        total={roster.total}
        onPageChange={roster.setPage}
        // The table's `footer` slot already draws the rule above the pagination.
        withSeparator={false}
      />
    ) : undefined;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <div className="space-y-3">
        <ColocaPageHeader
          title={t("page_title")}
          description={isInternalUser ? t("page_subtitle") : t("page_subtitle_single_org")}
          actions={
            <>
              <ColocaButton
                variant="secondary"
                size="sm"
                icon={RefreshCw}
                onClick={roster.refresh}
                disabled={roster.isLoading || roster.isRefetching}
              >
                {t("action_refresh")}
              </ColocaButton>
              {canWrite && (
                <ColocaButton variant="primary" size="sm" icon={Plus} onClick={ui.openCreate}>
                  {t("action_create")}
                </ColocaButton>
              )}
            </>
          }
        />
        <UsersNoRolesNote counts={roster.counts} />
      </div>

      <UsersPopulationStrip counts={roster.counts} value={roster.status} onChange={roster.setStatus} />

      {/* One disclosure each, never a toast: a toast would fire on every visit for a permission the operator
          cannot grant themselves. */}
      {isInternalUser && directoryFailed && (
        <ColocaAlert variant="warning" text={t("org_directory_failed")} />
      )}
      {isInternalUser && !directoryFailed && directoryTruncated && (
        <ColocaAlert variant="secondary" text={t("org_directory_truncated")} />
      )}
      {access.rolesUnavailable && !access.isLoadingRoles && (
        <ColocaAlert variant="secondary" text={t("filter_role_unavailable")} />
      )}

      <UsersToolbar
        query={roster.query}
        onQueryChange={roster.setQuery}
        isTermTooShort={roster.isTermTooShort}
        roleId={roster.roleId}
        onRoleChange={roster.setRoleId}
        roles={access.roles}
        rolesUnavailable={access.rolesUnavailable}
      />

      {roster.loadFailed ? (
        <ColocaAlert variant="destructive" text={t("error_load_failed")}>
          <ColocaButton variant="secondary" size="sm" onClick={roster.refresh}>
            {t("error_retry")}
          </ColocaButton>
        </ColocaAlert>
      ) : (
        <ColocaSurface padding="none" className="overflow-hidden">
          <div className="hidden lg:block">
            <UsersTable
              views={views}
              loading={roster.isLoading}
              isRefetching={roster.isRefetching}
              empty={empty}
              footer={footer}
              showOrganization={isInternalUser}
              isDirectoryPending={isDirectoryPending}
              pingedId={ui.pingedId}
            />
          </div>
          <div className="lg:hidden">
            {views.length === 0 && !roster.isLoading ? (
              <div className="p-6">{empty}</div>
            ) : (
              <UsersCards
                views={views}
                loading={roster.isLoading}
                showOrganization={isInternalUser}
                isDirectoryPending={isDirectoryPending}
                pingedId={ui.pingedId}
              />
            )}
            {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
          </div>
        </ColocaSurface>
      )}

      <CreateUserDrawer
        open={ui.overlay.kind === "create"}
        onOpenChange={(open) => !open && ui.closeOverlay()}
        isBusy={mutations.isBusy}
        error={ui.mutationError}
        onSubmit={handleCreate}
      />

      <UserDetailDrawer
        open={ui.overlay.kind === "detail"}
        onOpenChange={(open) => !open && ui.closeOverlay()}
        user={detail.user}
        isLoading={detail.isLoading}
        loadError={detail.error}
        initialSection={ui.overlay.kind === "detail" ? ui.overlay.section : null}
        organizationLabel={organizationLabel}
        roles={selectedUser ? (access.rolesById[selectedUser.id] ?? []) : []}
        summary={selectedUser ? access.summaryFor(selectedUser.id) : undefined}
        rolesUnavailable={access.rolesUnavailable}
        canWrite={canWrite}
        canManageRoles={canManageRoles}
        isBusy={mutations.isBusy}
        error={ui.mutationError}
        onSave={handleSave}
        onSetPassword={handleSetPassword}
        onManageAccess={() => selectedUser && ui.openAccess(selectedUser)}
      />

      {/* Reused verbatim from the access screen — it is generic over the principal and needs no context. What
          it must NOT do is reach `useAccessUsers`, which imports this module's service back. */}
      <AssignRolesDrawer
        open={ui.overlay.kind === "access"}
        onOpenChange={(open) => !open && ui.closeOverlay()}
        principal={
          accessPrincipal
            ? {
                id: accessPrincipal.id,
                name: accessPrincipal.full_name,
                kind: "person",
                subtitle: accessPrincipal.email,
              }
            : null
        }
        roles={access.roles}
        assignedRoleIds={assignedRoleIds}
        canManage={canManageRoles}
        onAssign={handleAssignRoles}
        onRemove={handleRemoveRole}
        canSplitCombinedRoles={isInternalUser}
      />

      <SuspendUserDialog
        user={ui.overlay.kind === "suspend" ? ui.overlay.user : null}
        isBusy={mutations.isBusy}
        onConfirm={() => void handleConfirmSuspend()}
        onOpenChange={(open) => !open && ui.closeOverlay()}
      />

      <DeleteUserDialog
        user={ui.overlay.kind === "delete" ? ui.overlay.user : null}
        isBusy={mutations.isBusy}
        onConfirm={handleDelete}
        onOpenChange={(open) => !open && ui.closeOverlay()}
      />
    </div>
  );
};

export default UsersPage;
